// 出荷ページ (mock UX 準拠 / 実API)。
//   /carry-out/                         一覧 (出荷一覧フラット / 出荷日グループ の2タブ・各出荷をAccordion)
//   /carry-out/new/                     出荷新規 (本体は情報なし=作成のみ)
//   /carry-out/:id/                     出荷詳細 (InfoList + ロット一覧)
//   /carry-out/:id/lot/new/             ロット新規 (工事→材料2段フィルタ/LotNo/製造日/予定QR数/備考)
//   /carry-out/:id/lot/:lotId/          ロット詳細
//   /carry-out/:id/lot/:lotId/edit/     ロット編集
//   /carry-out/:id/lot/:lotId/scan/     ロット単位QRスキャン (/process-lot)
// 当面オンライン限定。データは実API直 (api.carry_out / api.lot)。

import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PageContext } from "../PageContext";
import { MaterialDetailBase, MaterialDetailBaseSchema } from "../../../schema/generated/MaterialDetail";
import { WorkBase, WorkBaseSchema } from "../../../schema/generated/Work";
import { WorkSubtypeBase, WorkSubtypeBaseSchema } from "../../../schema/generated/WorkSubtype";
import { WorkDetailBase, WorkDetailBaseSchema } from "../../../schema/generated/WorkDetail";
import { MixRatioDefinitionBase, MixRatioDefinitionBaseSchema } from "../../../schema/generated/MixRatioDefinition";
import { MixRatioDetailBase, MixRatioDetailBaseSchema } from "../../../schema/generated/MixRatioDetail";
import { useMaterialMasters } from "../../../util/useMaterialMasters";
import { useIsOnline } from "../../../util/useIsOnline";
import { getRowsChecked } from "../../../util/getRowsChecked";
import { WorkMaterialFilterData, buildConstituents, buildDetailsByMaterial } from "./helpers";
import { CarryOutListPage } from "./CarryOutListPage";
import { CarryOutDetailPage } from "./CarryOutDetailPage";
import { CarryOutEditPage, CarryOutNewPage } from "./CarryOutFormPage";
import { LotFormPage } from "./LotFormPage";
import { LotDetailPage } from "./LotDetailPage";
import { LotScanPage } from "./LotScanPage";
import { errorMessageOf } from "../../../util/typed";

export default function CarryOutPage({ pageContext }: { pageContext: PageContext }) {
  const isOnline = useIsOnline(pageContext.api);
  const { materials: materialList, materialsById } = useMaterialMasters(pageContext.api);
  const [details, setDetails] = useState<MaterialDetailBase[]>([]);
  const [works, setWorks] = useState<WorkBase[]>([]);
  const [workSubtypes, setWorkSubtypes] = useState<WorkSubtypeBase[]>([]);
  const [workDetails, setWorkDetails] = useState<WorkDetailBase[]>([]);
  const [mixRatioDefinitions, setMixRatioDefinitions] = useState<MixRatioDefinitionBase[]>([]);
  const [mixRatioDetails, setMixRatioDetails] = useState<MixRatioDetailBase[]>([]);

  useEffect(() => {
    // オフライン時は取得しない。getRowsFromServer は IDB フォールバックが無く、
    // オフラインで開くたびにエラーダイアログが連発するため。オンライン復帰(isOnline 変化)で取得する。
    if (!isOnline) {
      return;
    }
    let active = true;
    getRowsChecked(pageContext.api, "material-details", MaterialDetailBaseSchema.base)
      .then((rows) => { if (active) { setDetails(rows); } })
      .catch((e) => {
        console.warn("material-details fetch failed", e);
        if (active) {
          pageContext.api.showError(`材料細目の取得に失敗しました。\n${errorMessageOf(e)}`);
        }
      });
    getRowsChecked(pageContext.api, "works", WorkBaseSchema.base)
      .then((rows) => { if (active) { setWorks(rows); } })
      .catch((e) => {
        console.warn("works fetch failed", e);
        if (active) { pageContext.api.showError(`工事マスタの取得に失敗しました。\n${errorMessageOf(e)}`); }
      });
    getRowsChecked(pageContext.api, "work-subtypes", WorkSubtypeBaseSchema.base)
      .then((rows) => { if (active) { setWorkSubtypes(rows); } })
      .catch((e) => {
        console.warn("work-subtypes fetch failed", e);
        if (active) { pageContext.api.showError(`種別マスタの取得に失敗しました。\n${errorMessageOf(e)}`); }
      });
    getRowsChecked(pageContext.api, "work-details", WorkDetailBaseSchema.base)
      .then((rows) => { if (active) { setWorkDetails(rows); } })
      .catch((e) => {
        console.warn("work-details fetch failed", e);
        if (active) { pageContext.api.showError(`工程マスタの取得に失敗しました。\n${errorMessageOf(e)}`); }
      });
    getRowsChecked(pageContext.api, "mix-ratio-definitions", MixRatioDefinitionBaseSchema.base)
      .then((rows) => { if (active) { setMixRatioDefinitions(rows); } })
      .catch((e) => {
        console.warn("mix-ratio-definitions fetch failed", e);
        if (active) { pageContext.api.showError(`配合比マスタの取得に失敗しました。\n${errorMessageOf(e)}`); }
      });
    getRowsChecked(pageContext.api, "mix-ratio-details", MixRatioDetailBaseSchema.base)
      .then((rows) => { if (active) { setMixRatioDetails(rows); } })
      .catch((e) => {
        console.warn("mix-ratio-details fetch failed", e);
        if (active) { pageContext.api.showError(`配合比明細の取得に失敗しました。\n${errorMessageOf(e)}`); }
      });
    return () => { active = false; };
  }, [pageContext, isOnline]);

  const constituentsById = useMemo(() => buildConstituents(details), [details]);
  const detailsByMaterial = useMemo(() => buildDetailsByMaterial(details), [details]);
  const workFilterData: WorkMaterialFilterData = useMemo(() => ({
    works: works.filter((w) => w.deleted_at == null),
    workSubtypes: workSubtypes.filter((s) => s.deleted_at == null),
    workDetails: workDetails.filter((d) => d.deleted_at == null),
    mixRatioDefinitions,
    mixRatioDetails,
    materialDetails: details.filter((d) => d.deleted_at == null),
  }), [works, workSubtypes, workDetails, mixRatioDefinitions, mixRatioDetails, details]);

  return (
    <Routes>
      <Route index element={<CarryOutListPage pageContext={pageContext} materialsById={materialsById} constituentsById={constituentsById} />} />
      <Route path="new/" element={<CarryOutNewPage pageContext={pageContext} />} />
      <Route path=":id/" element={<CarryOutDetailPage pageContext={pageContext} materialsById={materialsById} constituentsById={constituentsById} />} />
      <Route path=":id/edit/" element={<CarryOutEditPage pageContext={pageContext} />} />
      <Route path=":id/lot/new/" element={<LotFormPage pageContext={pageContext} materials={materialList} constituentsById={constituentsById} workFilterData={workFilterData} mode="new" />} />
      <Route path=":id/lot/:lotId/" element={<LotDetailPage pageContext={pageContext} materialsById={materialsById} detailsByMaterial={detailsByMaterial} />} />
      <Route path=":id/lot/:lotId/edit/" element={<LotFormPage pageContext={pageContext} materials={materialList} constituentsById={constituentsById} workFilterData={workFilterData} mode="edit" />} />
      <Route path=":id/lot/:lotId/scan/" element={<LotScanPage pageContext={pageContext} materialsById={materialsById} />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
